import { slipKindLabel } from 'utils/slip.utils';
import { Slip } from 'types/Slip';
import styles from './SlipEventsMobile.module.css';
import { TicketMultiBonus } from 'components/MultiBonus/MultiBonus';
import { useTranslation } from 'react-i18next';
import { CURRENCY } from 'App';
import classNames from 'classnames';
import { Dispatch, SetStateAction } from 'react';
import { usePlayerLayout } from 'lib/playerLayout';
import { CashoutButton } from 'components/Cashout/CashoutButton';
import { StornoButton } from 'components/Storno/StornoButton';
import { CashoutOffer } from 'services/api/slipService';

interface Props {
  slip: Slip;
  setShowSlipDetails: Dispatch<SetStateAction<Slip | null>>;
  /** The ticket last opened, marked so the player sees where they left off. */
  highlighted?: boolean;
  cashout?: CashoutOffer;
  onCashedOut?: () => void;
}

export const SlipEventsMobile = ({
  slip,
  setShowSlipDetails,
  highlighted = false,
  cashout,
  onCashedOut = () => {},
}: Props) => {
  const { t } = useTranslation();
  const slipStatus = String(slip.status ?? '').toLowerCase();
  const eventResults = slip.event_bets
    .map((x) => {
      const home = x.event.result.homeGoals;
      const away = x.event.result.awayGoals;
      return home != null && away != null ? `${home}:${away}` : null;
    })
    .filter(Boolean) as string[];
  const layout = usePlayerLayout();

  if (layout === 'modern') {
    const shown = slip.event_bets.slice(0, 2);
    return (
      <>
        <button
          type="button"
          className={classNames(styles.modernCard, {
            [styles.highlighted]: highlighted,
          })}
          onClick={() => setShowSlipDetails(slip)}
        >
          <span className={styles.modernHead}>
            <span>
              <strong>{slip.id}</strong>
              <small>{slip.created_at}</small>
            </span>
            <span
              className={classNames(styles.modernBadge, styles[slipStatus])}
            >
              {t(`${slipStatus}_label`, { defaultValue: slipStatus })}
              {slip.is_live_ticket && ' · LIVE'}
            </span>
            <span className={styles.modernArrow}>›</span>
          </span>
          {slip.awaiting_other_matches && (
            <span className={styles.modernNote}>
              {t('awaiting_other_matches_label')}
            </span>
          )}
          <span className={styles.modernMatches}>
            {shown.map((x, i) => (
              <span key={i}>
                {x.event.home} - {x.event.away}
              </span>
            ))}
            {slip.event_bets.length > shown.length && (
              <small>+{slip.event_bets.length - shown.length}</small>
            )}
          </span>
          <span className={styles.modernFigures}>
            <span>
              <small>{t('payment_label')}</small>
              <strong>
                {slip.stake} {CURRENCY}
              </strong>
            </span>
            <span>
              <small>{t('max_gain_label')}</small>
              <strong>
                {slip.payment} {CURRENCY}
              </strong>
            </span>
            <span>
              <small>{t('max_coefficient_label')}</small>
              <strong>{slip.maxCoefficient.toFixed(2)}</strong>
            </span>
          </span>
          <TicketMultiBonus
            percent={slip.multi_bonus_percent}
            amount={slip.multi_bonus_amount}
            compact
          />
        </button>
        {(slip.storno_left_ms ?? 0) > 0 && (
          <div className={styles.modernCashout}>
            <StornoButton slip={slip} onCancelled={onCashedOut} compact />
          </div>
        )}
        {cashout && cashout.reason !== 'storno' && (
          <div className={styles.modernCashout}>
            <CashoutButton offer={cashout} onCashedOut={onCashedOut} compact />
          </div>
        )}
      </>
    );
  }

  return (
    <>
      <div
        className={classNames(styles.wrapper, {
          [styles.highlighted]: highlighted,
        })}
        onClick={() => setShowSlipDetails(slip)}
      >
        <div>
          <span className={classNames(styles.slipStatus, styles[slipStatus])}>
            <div />
            {t(`${slipStatus}_label`, { defaultValue: slipStatus })}{' '}
            {slip.awaiting_other_matches && (
              <>({t('awaiting_other_matches_label')}) </>
            )}
            {slip.is_live_ticket && '*LIVE*'}
          </span>
          <span>{slip.created_at}</span>
          <span>
            {t('result_label')}:{' '}
            {eventResults.length > 0 ? eventResults.join(', ') : '-'}
          </span>
          <span>{slipKindLabel(slip, t)}</span>
        </div>
        <div>
          <span>
            {t('max_coefficient_label')}:{' '}
            <span className={styles.value}>
              {slip.maxCoefficient.toFixed(2)}
            </span>
          </span>
          <span>
            {t('payment_label')}:{' '}
            <span className={styles.value}>
              {slip.stake} {CURRENCY}
            </span>
          </span>
          <span>
            {t('max_gain_label')}:{' '}
            <span className={styles.value}>
              {slip.payment} {CURRENCY}
            </span>
          </span>
          <TicketMultiBonus
            percent={slip.multi_bonus_percent}
            amount={slip.multi_bonus_amount}
            compact
          />
          <div className={styles.chevronRight} />
        </div>
      </div>
      {(slip.storno_left_ms ?? 0) > 0 && (
        <div className={styles.cashOut}>
          <StornoButton slip={slip} onCancelled={onCashedOut} compact />
        </div>
      )}
      {cashout && cashout.reason !== 'storno' && (
        <div className={styles.cashOut}>
          <CashoutButton offer={cashout} onCashedOut={onCashedOut} compact />
        </div>
      )}
    </>
  );
};
